import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Centrifuge } from "centrifuge";
import { ApiContext } from "../../context/MyContext";
import { get as commsGet } from "../../service/comms/commsApi";

/**
 * Realtime (Centrifugo) provider for the Flex Comms module.
 *
 * Opens ONE websocket connection per signed-in user using a short-lived
 * connection token from the ms-agents backend. The socket follows auth state,
 * not page loads: login (an SPA navigation, no reload) opens it, logout closes
 * it, and signing in as someone else on the same tab reconnects. This socket is
 * also how ms-agents records every employee's presence sessions (online time on
 * the Coaches console), so a socket that outlived logout or never opened after
 * login mis-attributed or lost that time. The connection is best-effort: if the
 * Centrifugo host is unreachable or the token fetch fails, the app must still
 * render and `connected` stays false. This provider NEVER throws.
 *
 * Context value: { centrifuge, connected, connectionEpoch }
 *   - centrifuge: the Centrifuge instance (or null when unavailable)
 *   - connected: boolean reflecting the live socket state
 *   - connectionEpoch: increments on every (re)connect, so consumers can
 *     reconcile state that may have drifted while the socket was down
 */
export const RealtimeContext = createContext({
  centrifuge: null,
  connected: false,
  connectionEpoch: 0,
});

const CONNECTION_TOKEN_ENDPOINT = "/api/centrifugo/connection-token";

/**
 * Fetch a fresh connection token. Returns the raw payload
 * `{ token, ws_url, expires_in }` or throws on failure.
 */
async function fetchConnectionToken() {
  return commsGet(CONNECTION_TOKEN_ENDPOINT);
}

// Bootstrap (token fetch + connect) retries forever with capped exponential
// backoff — realtime must self-heal without a page reload even when the
// backend is down for minutes. Once `connected` fires, the Centrifuge
// client's own reconnect logic takes over.
const BOOTSTRAP_BACKOFF_BASE_MS = 1000;
const BOOTSTRAP_BACKOFF_MAX_MS = 30000;

/** Capped exponential backoff with ±25% jitter to avoid thundering herds. */
const backoffDelay = (attempt) =>
  Math.min(BOOTSTRAP_BACKOFF_MAX_MS, BOOTSTRAP_BACKOFF_BASE_MS * 2 ** attempt) *
  (0.75 + Math.random() * 0.5);

export const RealtimeProvider = ({ children }) => {
  const { isAuthenticated = false, empId = null } = useContext(ApiContext) ?? {};
  // Bumped to force a reconnect when a different user signs in on this tab
  // without logging out first.
  const [generation, setGeneration] = useState(0);
  // The employee the current connection belongs to; null until known.
  const ownerRef = useRef(null);
  const [centrifuge, setCentrifuge] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const clientRef = useRef(null);
  // Resolver of the in-flight backoff sleep, so a browser "online" event can
  // short-circuit the wait and retry immediately.
  const wakeRef = useRef(null);

  // `empId` hydrates asynchronously after a page load (null -> id for the SAME
  // user); adopting it must not reconnect. Only a switch between two known ids
  // is a different user.
  useEffect(() => {
    if (empId == null) return;
    const owner = ownerRef.current;
    ownerRef.current = empId;
    if (owner != null && owner !== empId) {
      setGeneration((g) => g + 1);
    }
  }, [empId]);

  useEffect(() => {
    // Realtime requires an authenticated session. Bail quietly otherwise.
    if (!isAuthenticated) {
      ownerRef.current = null;
      return undefined;
    }
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      return undefined;
    }

    let cancelled = false;

    // Cancellable sleep: resolves early when wakeRef is invoked (e.g. the
    // browser regains connectivity) or when the effect is torn down.
    const interruptibleSleep = (ms) =>
      new Promise((resolve) => {
        const timer = setTimeout(() => {
          wakeRef.current = null;
          resolve();
        }, ms);
        wakeRef.current = () => {
          clearTimeout(timer);
          wakeRef.current = null;
          resolve();
        };
      });

    const createClient = (wsUrl, token) => {
      const client = new Centrifuge(wsUrl, {
        token,
        // Auto-refresh: SDK calls this before the token expires.
        getToken: async () => {
          const next = await fetchConnectionToken();
          return next.token;
        },
      });

      client.on("connected", () => {
        if (!cancelled) {
          setConnected(true);
          setConnectionEpoch((epoch) => epoch + 1);
        }
      });
      client.on("disconnected", () => {
        if (!cancelled) {
          setConnected(false);
        }
      });
      client.on("error", (ctx) => {
        // Best-effort: log but never crash.
        console.error("Realtime connection error:", ctx);
      });

      return client;
    };

    const bootstrap = async () => {
      for (let attempt = 0; !cancelled; attempt += 1) {
        try {
          const conn = await fetchConnectionToken();
          if (cancelled) {
            return;
          }

          const wsUrl = conn?.ws_url || process.env.REACT_APP_CENTRIFUGO_URL;
          if (!wsUrl || !conn?.token) {
            // A cold backend can return an empty payload — retryable.
            throw new Error("Realtime: missing ws_url or connection token");
          }

          const client = createClient(wsUrl, conn.token);
          client.connect();
          clientRef.current = client;
          if (!cancelled) {
            setCentrifuge(client);
          }
          return;
        } catch (err) {
          console.error(
            `Realtime provider failed to initialize (attempt ${attempt + 1}):`,
            err
          );
          if (cancelled) {
            return;
          }
          setConnected(false);
          await interruptibleSleep(backoffDelay(attempt));
        }
      }
    };

    const handleOnline = () => {
      if (wakeRef.current) {
        wakeRef.current();
      }
    };
    window.addEventListener("online", handleOnline);

    bootstrap();

    return () => {
      cancelled = true;
      window.removeEventListener("online", handleOnline);
      if (wakeRef.current) {
        wakeRef.current();
      }
      const client = clientRef.current;
      if (client) {
        try {
          client.disconnect();
        } catch (err) {
          console.error("Realtime disconnect failed:", err);
        }
        clientRef.current = null;
      }
      setCentrifuge(null);
      setConnected(false);
    };
    // One connection per signed-in user: re-run on login/logout and on a
    // user switch, never on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, generation]);

  return (
    <RealtimeContext.Provider value={{ centrifuge, connected, connectionEpoch }}>
      {children}
    </RealtimeContext.Provider>
  );
};
